import { useContext, useEffect, useState } from 'react';

import styled from '@emotion/styled';
import toast from 'react-hot-toast';

import { PrimaryBlackButton } from '../../../components/Button/Buttons';
import StatsCard from '../../../components/Cards/StartsCard';
import BarChart from '../../../components/Charts/BarChart';
import { ActionRow, Muted, NoteInput, Row, Section, SectionTitle } from '../../../components/Detail/DetailParts';
import PageHeader from '../../../components/Layout/PageHeader';
import PageLayout from '../../../components/Layout/PageLayout';
import { Card, CardTitle, Select, Stack, StatsGrid, TwoColumn } from '../../../components/Layout/PagePieces';
import Page from '../../../components/Page/Page';
import Table from '../../../components/Table/Table';
import TableDateTimeFormatter from '../../../components/Table/TableAccessories/TableDateTimeFormatter';
import TableSliderContentWrapper from '../../../components/Table/TableAccessories/TableSliderContentWrapper';
import { TableContext } from '../../../components/Table/TableContext';
import useFeedbackSummary from '../../../hooks/dashboard/feedback/useFeedbackSummary';
import useUpdateFeedback from '../../../hooks/dashboard/feedback/useUpdateFeedback';
import { tableFormatDateTimeNG } from '../../../utils/utils';
import { TransactionDetailBody } from '../Transactions/TransactionDetailSlider';

const TOPICS: Record<string, string> = {
  purchase: 'A purchase',
  card: 'Paying with my card',
  chat: 'Chatting with Hethera',
  ejor: 'Ejor',
  dash: 'Dash',
  schedules: 'Scheduled payments',
  other: 'Something else',
  none: 'No topic',
};
const RATING_EMOJI: Record<number, string> = { 1: '😞 Bad', 2: '😐 Okay', 3: '😊 Great' };

// What users told us (limits spec F5). Comments are the user's own words,
// shown as plain text (React escapes them), never sent to the model.
const Feedback = () => {
  const { summary, fetchSummary } = useFeedbackSummary();
  const [tableKey, setTableKey] = useState(0);

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line
  }, []);

  const total = Object.values(summary?.by_rating ?? {}).reduce((a, b) => a + b, 0);
  const great = summary?.by_rating?.Great ?? 0;

  return (
    <Page title='Hethera | Feedback'>
      <PageLayout>
        <Stack>
          <PageHeader title='Feedback' className='borderless no-bottom-padding' />
          <StatsGrid>
            <StatsCard.Root>
              <StatsCard.Header>Satisfaction (12 weeks)</StatsCard.Header>
              <StatsCard.Value>{total ? `${Math.round((100 * great) / total)}%` : '—'}</StatsCard.Value>
              <StatsCard.PlainFooter>Share of ratings that were Great</StatsCard.PlainFooter>
            </StatsCard.Root>
            {['Great', 'Okay', 'Bad'].map((label) => (
              <StatsCard.Root key={label}>
                <StatsCard.Header>{label}</StatsCard.Header>
                <StatsCard.Value>{summary?.by_rating?.[label] ?? 0}</StatsCard.Value>
                <StatsCard.PlainFooter>ratings in 12 weeks</StatsCard.PlainFooter>
              </StatsCard.Root>
            ))}
          </StatsGrid>

          <TwoColumn>
            <Card>
              <CardTitle>Satisfaction by week</CardTitle>
              <BarChart
                data={(summary?.weeks ?? []).map((w) => ({ label: w.week.slice(5), satisfaction: w.satisfaction_pct }))}
                dataKeys={['satisfaction']}
                xAxisDataKey='label'
                fills={['#1F7A45']}
                tooltipLabel={['% Great']}
                showLegend={false}
                aspect={2}
              />
            </Card>
            <Card>
              <CardTitle>Newest bad comments</CardTitle>
              {(summary?.newest_bad ?? []).map((f) => (
                <Comment key={f.id}>
                  <p>{f.comment}</p>
                  <Muted>
                    {TOPICS[f.topic ?? 'none']} · {tableFormatDateTimeNG(f.created_at)}
                  </Muted>
                </Comment>
              ))}
              {!summary?.newest_bad?.length && <Muted>None. Good.</Muted>}
            </Card>
          </TwoColumn>

          <Table
            reloadSignal={tableKey}
            tableHeader={{ Date: 'created_at', Customer: 'user_name', Rating: 'rating', Topic: 'topic', Comment: 'comment', Status: 'status' }}
            tableDataSourceUrl='api/v1/admin/feedback'
            size={20}
            border
            tableFilterBoxFeatures={{ rightAttr: [{ type: 'custom-filter', comp: <FeedbackFilters /> }] }}
            cellsCustomComp={{
              created_at: <TableDateTimeFormatter dataKey='created_at' />,
              user_name: <Cell render={(d) => d.user_name ?? d.chat_id} />,
              rating: <Cell render={(d) => RATING_EMOJI[d.rating] ?? '—'} />,
              topic: <Cell render={(d) => TOPICS[d.topic ?? 'none']} />,
              comment: <Cell render={(d) => <span className='cell-text' title={d.comment ?? ''}>{d.comment ?? '—'}</span>} />,
              status: <Cell render={(d) => d.status} />,
            }}
            clickRowShowSliderComp={(row) => <FeedbackSlider data={row} onChanged={() => setTableKey((k) => k + 1)} />}
            hasClickableRow
            emptyStateMsg='No feedback yet'
          />
        </Stack>
      </PageLayout>
    </Page>
  );
};

export default Feedback;

const Cell: React.FC<{ data?: Record<string, any>; render: (d: Record<string, any>) => React.ReactNode }> = ({ data, render }) => (
  <>{data ? render(data) : null}</>
);

const FeedbackFilters = () => {
  const table = useContext(TableContext);
  const set = (key: string, value: string) => table?.handleSetTableFilterRecord({ [key]: value, page: 0 });
  return (
    <Filters>
      <Select defaultValue='' aria-label='Rating' onChange={(e) => set('rating', e.target.value)}>
        <option value=''>All ratings</option>
        <option value='1'>Bad</option>
        <option value='2'>Okay</option>
        <option value='3'>Great</option>
      </Select>
      <Select defaultValue='' aria-label='Status' onChange={(e) => set('status', e.target.value)}>
        <option value=''>Any status</option>
        <option value='NEW'>New</option>
        <option value='REVIEWED'>Reviewed</option>
        <option value='ACTIONED'>Actioned</option>
      </Select>
      <Select defaultValue='' aria-label='Comment' onChange={(e) => set('has_comment', e.target.value)}>
        <option value=''>With or without a comment</option>
        <option value='true'>With a comment</option>
      </Select>
    </Filters>
  );
};

const FeedbackSlider: React.FC<{ data: Record<string, any>; onChanged: () => void }> = ({ data, onChanged }) => {
  const { saving, update } = useUpdateFeedback();
  const [note, setNote] = useState<string>(data.admin_note ?? '');
  const [showPurchase, setShowPurchase] = useState(false);

  const save = (status?: string) =>
    update(data.id, { status, note }, () => {
      toast.success('Saved');
      onChanged();
    });

  return (
    <TableSliderContentWrapper label='Feedback'>
      <Section>
        <strong>{RATING_EMOJI[data.rating] ?? 'No rating'}</strong>
        <Muted>
          {data.user_name ?? data.chat_id} · {tableFormatDateTimeNG(data.created_at)} · from {data.source.replace('_', ' ')}
        </Muted>
      </Section>
      <Section>
        <SectionTitle>What they said</SectionTitle>
        <CommentBody>{data.comment ?? 'No comment.'}</CommentBody>
        <Row label='Topic'>{TOPICS[data.topic ?? 'none']}</Row>
        <Row label='Can we contact them'>{data.contact_ok ? `Yes, on ${data.chat_id}` : 'No'}</Row>
        {data.transaction_reference && (
          <Row label='Purchase'>
            <LinkButton type='button' onClick={() => setShowPurchase(!showPurchase)}>
              {data.transaction_reference}
            </LinkButton>
          </Row>
        )}
      </Section>
      {showPurchase && data.transaction_reference && <TransactionDetailBody reference={data.transaction_reference} />}
      <Section>
        <SectionTitle>Review</SectionTitle>
        <NoteInput rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder='Internal note' />
        <ActionRow>
          <PrimaryBlackButton type='button' disabled={saving} onClick={() => save('REVIEWED')}>
            Mark reviewed
          </PrimaryBlackButton>
          <PrimaryBlackButton type='button' disabled={saving} onClick={() => save('ACTIONED')}>
            Mark actioned
          </PrimaryBlackButton>
        </ActionRow>
      </Section>
    </TableSliderContentWrapper>
  );
};

const Filters = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Comment = styled.div`
  border-bottom: 1px solid #efece4;
  padding-bottom: 10px;

  p {
    margin: 0 0 4px;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
`;

const CommentBody = styled.p`
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const LinkButton = styled.button`
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  color: ${({ theme }) => theme.palette.greenColor};
  text-decoration: underline;
  cursor: pointer;
`;
